import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FormikProvider } from 'formik';

import { usePropertyDetail } from './hooks/usePropertyDetail';
import { usePropertyDetailForm } from './hooks/usePropertyDetailForm';
import { PropertyDetailModeContext } from './hooks/usePropertyDetailMode';
import { PropertyDetailHeader } from './components/PropertyDetailHeader';
import { PropertyHeroCard } from './components/PropertyHeroCard';
import { PropertyDetailTabs } from './components/PropertyDetailTabs';
import { PropertyOrdersCard } from './components/PropertyOrdersCard';
import { PropertyDetailSkeleton } from './components/PropertyDetailSkeleton';
import { SyncManagedFooter } from './components/SyncManagedFooter';
import { DiscardChangesDialog } from '@/components/ui/discard-changes-dialog';
import { useDirtyStateBlocker } from '@/hooks/useDirtyStateBlocker';
import { ROUTES, buildRoute } from '../../../config/routes';
import { showAlert } from '../../../slice/AlertSlice';
import { Variants } from '../../../types/common';
import { initiateOrgSwitch } from '../../../services/org-data';
import { useAddPropertiesToCart } from '../property-view/hooks/useAddPropertiesToCart';
import { setCartCount } from '../property-view/slice/OrderCartSlice';
import { useGetCartQuery } from '../../orders/services/orderApi';
import { getPermissionForAction } from '../../../util/permissions';

import type { HeroSource } from '../property-view/property-detail-drawer/types';
import type { PropertyEntity } from '../../../types/property.types';
import { pickLatestPhotoset, PropertyLoadError, PropertyNotFound } from '../property-shared';

export default function PropertyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const detail = usePropertyDetail(id ?? '');

  if (!id) return null;

  if (detail.isLoading) return <PropertyDetailSkeleton />;

  if (detail.isNotFound) {
    return (
      <PropertyNotFound
        currentOrgName={detail.currentOrgName}
        onBackToProperties={() => navigate(ROUTES.PROPERTIES)}
        onSwitchOrg={() => navigate(initiateOrgSwitch())}
      />
    );
  }

  if (detail.isError || !detail.property) {
    return (
      <PropertyLoadError
        onRetry={detail.refetch}
        onBackToProperties={() => navigate(ROUTES.PROPERTIES)}
      />
    );
  }

  // Hand off to PropertyDetailContent — it owns the form/state/effect hooks,
  // mounted only when `property` is loaded so its hooks fire unconditionally.
  return (
    <PropertyDetailContent
      id={id}
      property={detail.property}
      detail={detail}
      navigate={navigate}
      dispatch={dispatch}
    />
  );
}

interface ContentProps {
  id: string;
  property: PropertyEntity;
  detail: ReturnType<typeof usePropertyDetail>;
  navigate: ReturnType<typeof useNavigate>;
  dispatch: ReturnType<typeof useDispatch>;
}

function PropertyDetailContent({ id, property, detail, navigate, dispatch }: ContentProps) {
  const location = useLocation();
  const formik = usePropertyDetailForm({ property, setMode: detail.setMode });
  const [discardOpen, setDiscardOpen] = useState(false);
  const routeBlocker = useDirtyStateBlocker(detail.mode === 'edit' && formik.dirty);
  const addPropertiesToCart = useAddPropertiesToCart();
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const userId = localStorage.getItem('userId') ?? '';
  // Skip for roles denied 'GET :Cart' (INTERNAL_POST_PROCESSING) so a 403 can't
  // trigger baseQueryWithAuth's refresh-token rotation / spurious logout (DV-2751).
  const { data: cart } = useGetCartQuery(userId, {
    skip: !userId || !getPermissionForAction('Cart', 'GET'),
  });
  const isInCart = !!cart?.items?.some((item) => item.propertyId === property.propId);

  // Honor the "open in edit mode" intent set by the properties-table quick
  // action (router state `{ mode: 'edit' }`). Mount-only — the routing intent
  // is one-shot, and we clear it from history so a refresh returns to view
  // instead of trapping the user in an edit session against a fresh form.
  useEffect(() => {
    const requestedMode = (location.state as { mode?: string } | null)?.mode;
    if (requestedMode !== 'edit' || !detail.canEdit) return;
    detail.setMode('edit');
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!formik.dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      // Trigger the browser's native "leave site?" prompt.
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [formik.dirty]);

  const totalElements = detail.ordersQuery.data?.totalElements ?? 0;

  const heroSource: HeroSource = {
    logoUrl: property.logoUrl ?? null,
    photoset: pickLatestPhotoset(detail.photosetQuery.currentData) ?? null,
    isPhotosetLoading: detail.photosetQuery.isFetching,
  };

  return (
    <PropertyDetailModeContext.Provider
      value={{ mode: detail.mode, isSyncManaged: detail.isSyncManaged }}
    >
      <FormikProvider value={formik}>
        <section className="flex h-full flex-col bg-background">
          <PropertyDetailHeader
            mode={detail.mode}
            canEdit={detail.canEdit}
            isAddingToCart={isAddingToCart}
            isInCart={isInCart}
            hasPhotoset={property.hasPhotoset === true}
            onBack={() => navigate(ROUTES.PROPERTIES)}
            onManageAssets={() => navigate(buildRoute.propertyAssets(property.propId))}
            onAddToCart={() => {
              if (isAddingToCart) return;
              setIsAddingToCart(true);
              addPropertiesToCart([property.propId])
                .then(({ itemsCount, addedCount }) => {
                  dispatch(setCartCount(itemsCount));
                  if (addedCount > 0) {
                    dispatch(
                      showAlert({
                        type: Variants.SUCCESS,
                        message: '1 property successfully added to cart',
                      }),
                    );
                  }
                })
                .catch((e: unknown) => console.error('Error adding to cart', e))
                .finally(() => setIsAddingToCart(false));
            }}
            onEdit={() => detail.setMode('edit')}
            onSave={() => formik.submitForm()}
            onCancel={() => {
              if (formik.dirty) {
                setDiscardOpen(true);
              } else {
                detail.setMode('view');
              }
            }}
          />

          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
            <PropertyHeroCard
              property={property}
              heroSource={heroSource}
              isSyncManaged={detail.isSyncManaged}
            />

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(0,360px)] gap-6">
              <div className="rounded-lg border border-border bg-card p-6">
                <PropertyDetailTabs property={property} />
              </div>
              <PropertyOrdersCard
                propertyId={id}
                totalElements={totalElements}
                onViewAll={() => {
                  const address = encodeURIComponent(property.address1 ?? '');
                  navigate(`/home/orders?propertyAddress=${address}`);
                }}
              />
            </div>

            {detail.isSyncManaged && (
              <SyncManagedFooter pmsSource={property.pmsSource} />
            )}
          </div>
          <DiscardChangesDialog
            open={discardOpen}
            onKeepEditing={() => setDiscardOpen(false)}
            onDiscard={() => {
              formik.resetForm();
              detail.setMode('view');
              setDiscardOpen(false);
            }}
          />
          <DiscardChangesDialog
            open={routeBlocker.isBlocking}
            onKeepEditing={routeBlocker.cancel}
            onDiscard={() => {
              formik.resetForm();
              routeBlocker.proceed();
            }}
          />
        </section>
      </FormikProvider>
    </PropertyDetailModeContext.Provider>
  );
}
